import { BoxV2 } from '@labrav/react-components'
import { DiscountType } from '../../../../types/flyout'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShelves } from '@fortawesome/pro-regular-svg-icons'
import type { Installments } from '../../../../modules/privateOffer/reducer'
import { FormikProps, useFormikContext } from 'formik'
import {
  ChannelPartnerPrivateOfferInCreation,
  CppoPriceAdjustmentType,
} from '../../../../modules/channelPartnerPrivateOffer/reducer'
import { PaymentScheduleV2 } from '../../../../common/components/PaymentScheduleV2/PaymentScheduleV2'
import { getMarkupValues } from '../../../../utils/channelOfferUtil'
import { InstallmentsInfoTable } from './InstallmentsInfoTableView'
import type { ReceivedResellerAuthorization } from '../../../../modules/receivedResellerAuthorization/reducer'
import { isNonEmptyNumber } from '@labrav/common'
import { useStyles } from './InstallmentsInfo.styles'
import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { getRandom } from '../../../../common/utils/helperFunctions'
import { awsMaxScheduledInstallments } from '../../../../common/utils/constants'

export const InstallmentsInfo = ({
  discountType,
  resaleAuthorization,
  isAbo,
  remainingInstallementAmount,
}: {
  discountType?: DiscountType
  resaleAuthorization?: ReceivedResellerAuthorization
  isAbo?: boolean
  remainingInstallementAmount?: number
}) => {
  const { classes } = useStyles()
  const formikProps =
    useFormikContext() as FormikProps<ChannelPartnerPrivateOfferInCreation>
  const { values, setFieldValue } = formikProps

  const [randomKey, setRandomKey] = useState<number>(getRandom())
  useEffect(() => {
    setRandomKey(getRandom())
  }, [
    values?.flexiblePaymentTerms,
    values?.adjustmentPercentage,
    values?.dimensions,
  ])

  const isFixedMarkup =
    values?.priceAdjustmentType === CppoPriceAdjustmentType.FIXED

  const { totalDueToIsv, markupOrTotal } = getMarkupValues({
    dimensions: values?.dimensions,
    adjustmentPercentage: values?.adjustmentPercentage,
    priceAdjustmentType: values?.priceAdjustmentType,
    resaleAuthorization,
  })

  const handleChangeInstallmentsAmount = async (
    installmentsList: Installments[]
  ) => {
    const totalAmount = installmentsList?.reduce(
      (acc, next) => acc + (Number(next?.amount) || 0),
      0
    )

    const valueData =
      values?.priceAdjustmentType === CppoPriceAdjustmentType.FIXED
        ? totalAmount
        : Number(
            (((totalAmount - totalDueToIsv!) / totalDueToIsv!) * 100).toFixed(2)
          )
    await setFieldValue('adjustmentPercentageInputValue', valueData)

    if (isAbo) {
      const installments = installmentsList?.map(item => ({
        ...item,
        otherAmount: isNonEmptyNumber(totalDueToIsv)
          ? parseFloat((totalDueToIsv! / installmentsList.length).toFixed(2))
          : '-',
      }))
      await setFieldValue('installments', installments)
    }
  }
  return (
    <BoxV2
      heading={'Installments'}
      headingIcon={
        <FontAwesomeIcon
          icon={faShelves}
          style={{ width: '18px', height: '14px' }}
        />
      }
    >
      {isNonEmptyNumber(remainingInstallementAmount) && (
        <div>
          <div className={classes.heading} data-testid="pending-amount">
            Pending amount on current subscription
          </div>
          <div
            className={clsx(classes.price, classes.leftAlign)}
            data-testid={'pending-amount-value'}
          >
            <span className={classes.currency}>$</span>
            {remainingInstallementAmount}
          </div>
        </div>
      )}
      {discountType === DiscountType.FLEXIBLE_PAYMENT_TERMS ? (
        <InstallmentsInfoTable
          installments={values?.installments}
          editable={isFixedMarkup}
        />
      ) : values?.flexiblePaymentTerms ? (
        <PaymentScheduleV2
          formik={
            formikProps as unknown as FormikProps<Record<string, unknown>>
          }
          addButtonName={'Add more installments'}
          buttonCounterName={'installments'}
          shouldShowTotal={true}
          installmentsLimit={awsMaxScheduledInstallments}
          sectionDescription={''}
          tcvKey="adjustmentPercentageInputValue"
          updatedRandomKey={randomKey}
          tcv={
            !isFixedMarkup
              ? (markupOrTotal as number)
              : values?.adjustmentPercentageInputValue
          }
          handleChangeInstallmentsAmount={handleChangeInstallmentsAmount}
          fixTcvView
          {...(isAbo
            ? {
                totalOtherAmount: totalDueToIsv,
                customColumns: [
                  {
                    header: 'Payment',
                    keyName: 'index',
                  },
                  {
                    header: 'Payment to ISV',
                    keyName: 'otherAmount',
                  },
                ],
              }
            : {})}
        />
      ) : (
        <></>
      )}
    </BoxV2>
  )
}
